import React, { useState, useEffect, useMemo, useRef, MouseEventHandler, ReactNode, Dispatch, SetStateAction, useContext } from 'react';
import { useTheme } from '@emotion/react';
import {
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  Input,
  Spinner,
  Alert,
  Table,
  HoverLabelButton,
} from 'src/components/ui';
import Select from 'react-select';
import CustomBadge from '../components/CustomBadge';
import FilterChip from '../components/FilterChip';
import { useDarkMode } from '../context/DarkModeContext';
import { Trans, useLingui } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import Loader from '../components/Loader';
import { EmptyState } from '../components/Icons/EmptyStates';
import DataRemarkModal from './modals/DataRemarkModal';
import ResolveDataRemarkModal from './modals/ResolveDataRemarkModal';
import MessageThread from './MessageThread';
import InlineCompose from './InlineCompose';
import FileApi from '../api/FileApi';
import { AccordionItem } from '../components/ui/Accordion';
import {
  categoryKey,
  getRemarkCategoryLabel,
  getScheduleKeysForRemark,
  isGeneralRemark,
  isRemarkResponded,
  ScheduleOption,
  useDataRemarks,
} from './useDataRemarks';
import { DataRemark, MAX_DATA_REMARK_MESSAGE_LENGTH } from '../api/DataRemarksApi';
import { WFAccessToken } from '../api/types';
import { FormField } from '../util/extractFormFieldsFromSchema';
import dayjs from 'dayjs';
import classnames from '../util/classnames';
import { AuthContext, AuthContextValue } from '../context/AuthContext';
import { getTestId } from '../hooks/useTestId';
import Icon from 'src/components/Icon';

// ─── Theme ────────────────────────────────────────────────────────────────────

type UWETheme = {
  primaryMain: string;
  successMain: string;
  warningMain: string;
  dangerMain: string;
  infoMain: string;
  [key: string]: string;
};

export const CATEGORY_COLORS: Record<CategoryFilter, string> = {
  'Cross-Schedule': '#4a90e2',
  'Red Flag': '#ED5594',
  Manual: '#9b59b6',
  Amendment: '#c97608',
  'Modification Required': '#33c2a3',
};

export const STATUS_COLORS: Record<StatusFilter, string> = {
  Active: '#e05252',
  Responded: '#e6a817',
  Resolved: '#28a745',
};

const canEditRemark = (remark: DataRemark, auth: AuthContextValue) => {
  return remark?.createdBy?.id == auth?.user?.user?.id;
};

// ─── Styled components ────────────────────────────────────────────────────────

const formatValue = (val: any): string => {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
};

const formatStoredValue = (raw: string): string => {
  if (!raw) return '—';
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.map(formatValue).join(', ');
    return formatValue(parsed);
  } catch {
    return raw;
  }
};

export const renderValuesTable = (
  remark: DataRemark,
  getFieldValueForPath: any,
  availableFieldsByDataPath: any,
  availableFieldsByPath: any,
  navigateToField: any = null,
  navigateBtnColor: any = null
) => {
  const paths = remark.fieldPath;
  const values = remark.fieldValue;
  const previousValues = remark.fieldPreviousValue;
  const hasPrevious = previousValues?.some(v => v);
  if (!paths?.length) return null;

  return (
    <div className="mb-3">
      <Table size="sm" striped responsive className="mb-0" style={{ fontSize: '0.8rem', tableLayout: 'fixed', background: 'transparent' }}>
        <colgroup>
          {hasPrevious ? (
            <>
              <col style={{ width: '34%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '22%' }} />
            </>
          ) : (
            <>
              <col style={{ width: '44%' }} />
              <col style={{ width: '28%' }} />
              <col style={{ width: '28%' }} />
            </>
          )}
        </colgroup>
        <thead style={{ backgroundColor: 'transparent' }}>
          <tr style={{ backgroundColor: 'transparent' }}>
            <th style={{ backgroundColor: 'transparent' }}>
              <Trans>Field</Trans>
            </th>
            {hasPrevious && (
              <th style={{ backgroundColor: 'transparent', textAlign: 'center' }}>
                <Trans>Previous report</Trans>
              </th>
            )}
            <th style={{ backgroundColor: 'transparent', textAlign: 'center' }}>
              <Trans>Value at creation</Trans>
            </th>
            <th style={{ backgroundColor: 'transparent', textAlign: 'center' }}>
              <Trans>Current value</Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          {paths.map((path, i) => {
            const field = availableFieldsByDataPath?.[path] ?? availableFieldsByPath?.[path];
            const storedDisplay = formatStoredValue(values?.[i]);
            const previousDisplay = formatStoredValue(previousValues?.[i] ?? '');
            const currentRaw = getFieldValueForPath(path);
            const currentDisplay = formatValue(currentRaw);
            const fieldLabel = field?.description || field?.name || path;
            const fieldSubtitle = field?.description ? field.name : undefined;
            return (
              <tr key={`${path}-${i}`}>
                <td style={{ overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={fieldLabel}>
                        {fieldLabel}
                      </div>
                      {fieldSubtitle && (
                        <small
                          className="text-muted"
                          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                        >
                          {fieldSubtitle}
                        </small>
                      )}
                    </div>
                    {field && navigateToField && (
                      <button
                        type="button"
                        title={t`Go to field`}
                        onClick={e => {
                          e.stopPropagation();
                          navigateToField(field.id, 'danger');
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          padding: '2px 3px',
                          cursor: 'pointer',
                          color: navigateBtnColor,
                          flexShrink: 0,
                          lineHeight: 1,
                        }}
                        {...getTestId('data-remarks', '.', 'go-to-field', '-', i + 1)}
                      >
                        <Icon icon="Crosshair" style={{ fontSize: '0.7rem' }} />
                      </button>
                    )}
                  </div>
                </td>
                {hasPrevious && (
                  <td style={{ textAlign: 'center' }}>
                    <code className="small">{previousDisplay}</code>
                  </td>
                )}
                <td style={{ textAlign: 'center' }}>
                  <code className="small">{storedDisplay}</code>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <code className="small">{currentDisplay}</code>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

// ─── Types ────────────────────────────────────────────────────────────────────

type UWEEntityDataRemarksProps = {
  entity: { id: number } | null;
  auth: AuthContextValue;
  readOnly?: boolean;
  wfAccessToken: WFAccessToken;
  formSchema: Record<string, any>;
  mode?: 'review' | 'response' | 'amendment-review' | 'amendment-response' | 'amendment';
  /** Current panel layout mode — passed by SidebarComponents to adjust filter bar layout */
  panelMode?: PanelMode;
  /** Last known filter/selection state — restored on remount after mode switch */
  initialFilterState?: FilterState | null;
  /** Called whenever filter or selection state changes so the parent can persist it */
  onFilterStateChange?: (state: FilterState) => void;
};

type CategoryFilter = 'Cross-Schedule' | 'Red Flag' | 'Manual' | 'Amendment' | 'Modification Required';
type StatusFilter = 'Active' | 'Responded' | 'Resolved';
type PanelMode = 'right' | 'bottom' | 'float';
type BulkActionType = 'resolve' | 'comment';

type FilterState = {
  activeCategories: Set<CategoryFilter>;
  activeStatuses: Set<StatusFilter>;
  activeSchedules: Set<string>;
  selectedIds: Set<number>;
};

// ─── Constants ────────────────────────────────────────────────────────────────

// Built as functions (not module-level constants) so the `t` macro re-reads the active locale on
// every call — a frozen constant would bake in whatever locale was active when the module first
// loaded and never update when the user switches language.
const getCategoryFiltersRegular = (): { id: CategoryFilter; label: string }[] => [
  { id: 'Cross-Schedule', label: t`Cross-Schedule` },
  { id: 'Red Flag', label: t`Red Flag` },
  { id: 'Manual', label: t`Manual` },
];

const getCategoryFiltersAmendments = (): { id: CategoryFilter; label: string }[] => [
  { id: 'Cross-Schedule', label: t`Cross-Schedule` },
  { id: 'Red Flag', label: t`Red Flag` },
  { id: 'Manual', label: t`Manual` },
  { id: 'Amendment', label: t`Amendment` },
  { id: 'Modification Required', label: t`Modification Required` },
];

const getStatusFilters = (): { id: StatusFilter; label: string }[] => [
  { id: 'Active', label: t`Active` },
  { id: 'Responded', label: t`Responded` },
  { id: 'Resolved', label: t`Resolved` },
];

const FILTER_GROUP_LABEL: React.CSSProperties = {
  fontSize: '0.65rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '0.25rem',
};

// ─── Utilities ────────────────────────────────────────────────────────────────

const toErrorMessage = (err: any): string | null => err?.message || err?.result || (err ? JSON.stringify(err) : null);

export function getRemarkStatusKey(remark: DataRemark, auth: AuthContextValue, mode: string): StatusFilter {
  if (remark?.isResolved) return 'Resolved';
  if (isRemarkResponded(remark, auth, mode)) return 'Responded';
  return 'Active';
}

export const remarkStatus = (remark: DataRemark, auth: AuthContextValue, mode: string): { color: string; label: ReactNode; key: string } => {
  if (remark?.isResolved) return { color: '#28a745', label: <Trans>Resolved</Trans>, key: 'Resolved' };
  if (isRemarkResponded(remark, auth, mode)) return { color: '#e6a817', label: <Trans>Responded</Trans>, key: 'Responded' };
  return { color: '#ED5594', label: <Trans>Active</Trans>, key: 'Active' };
};

// ─── Main component ───────────────────────────────────────────────────────────

function UWEEntityDataRemarks({
  entity,
  auth,
  readOnly,
  wfAccessToken,
  formSchema,
  mode = 'review',
  panelMode = 'right',
  initialFilterState,
  onFilterStateChange,
}: UWEEntityDataRemarksProps) {
  const {
    dataRemarks,
    subforms,
    availableSchedules,
    navigateToField,
    availableFieldsByPath,
    availableFieldsByDataPath,
    selectedRemark,
    setSelectedRemark,
    permissions,
    loading,
    modalLoading,
    showCreateModal,
    openCreateModal,
    newRemark,
    setNewRemark,
    editingRemark,
    handleModalClose,
    handleCreateRemark,
    handleUpdateRemark,
    handleEditRemark,
    handleRemarkClick,
    handleAddMessageToRemark,
    handleAddMessageToEditingRemark,
    errorCreatingRemark,
    showResolveModal,
    setShowResolveModal,
    remarkToResolve,
    setRemarkToResolve,
    handleResolveRemark,
    handleSubmitResolution,
    errorResolvingRemark,
    handleBulkResolve,
    handleBulkComment,
    getFieldValueForPath,
  } = useDataRemarks({ entity, auth, wfAccessToken, formSchema, mode, readOnly });

  const { canAddRemarks, canEditRemarks, canResolveRemarks, canRespondToRemarks } = permissions;

  // ─── Filter state (initialised from parent-persisted state on remount) ──
  const CATEGORY_FILTERS = mode.includes('amendment') ? getCategoryFiltersAmendments() : getCategoryFiltersRegular();
  const [activeCategories, setActiveCategories] = useState<Set<CategoryFilter>>(
    () => initialFilterState?.activeCategories ?? new Set<CategoryFilter>(CATEGORY_FILTERS.map(f => f.id))
  );
  const [activeStatuses, setActiveStatuses] = useState<Set<StatusFilter>>(
    () => initialFilterState?.activeStatuses ?? new Set<StatusFilter>(['Active', 'Responded'])
  );
  const [activeSchedules, setActiveSchedules] = useState<Set<string>>(() => initialFilterState?.activeSchedules ?? new Set<string>());

  // ─── Bulk selection state ────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => initialFilterState?.selectedIds ?? new Set<number>());
  const [bulkAction, setBulkAction] = useState<BulkActionType | null>(null);
  const [bulkResolutionNotes, setBulkResolutionNotes] = useState('');
  const [bulkCommentText, setBulkCommentText] = useState('');
  const [bulkAttachmentFile, setBulkAttachmentFile] = useState<File | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const toggleCategory = (cat: CategoryFilter) =>
    setActiveCategories(prev => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });

  const toggleStatus = (s: StatusFilter) =>
    setActiveStatuses(prev => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });

  const toggleSchedule = (key: string) =>
    setActiveSchedules(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const clearFilters = () => {
    setActiveCategories(new Set(CATEGORY_FILTERS.map(f => f.id)));
    setActiveStatuses(new Set(['Active', 'Responded']));
    setActiveSchedules(new Set());
  };

  const isFiltersActive =
    activeCategories.size !== CATEGORY_FILTERS.length ||
    CATEGORY_FILTERS.some(({ id }) => !activeCategories.has(id)) ||
    activeStatuses.size !== 2 ||
    !activeStatuses.has('Active') ||
    !activeStatuses.has('Responded') ||
    activeSchedules.size > 0;

  // ─── Filtered list ───────────────────────────────────────────────────────
  const filteredDataRemarks = useMemo(
    () =>
      dataRemarks.filter(
        r =>
          activeStatuses.has(getRemarkStatusKey(r, auth, mode)) &&
          activeCategories.has(categoryKey(r.category)) &&
          (activeSchedules.size === 0 || getScheduleKeysForRemark(r, subforms).some(k => activeSchedules.has(k)))
      ),
    [dataRemarks, activeStatuses, activeCategories, activeSchedules, subforms]
  );

  // ─── Persist filter/selection state to parent (survives mode remounts) ──
  useEffect(() => {
    onFilterStateChange?.({ activeCategories, activeStatuses, activeSchedules, selectedIds });
  }, [activeCategories, activeStatuses, activeSchedules, selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Auto-deselect hidden cards ──────────────────────────────────────────
  useEffect(() => {
    const visibleIds = new Set(filteredDataRemarks.map(r => r.id!));
    setSelectedIds(prev => {
      if ([...prev].every(id => visibleIds.has(id))) return prev;
      return new Set([...prev].filter(id => visibleIds.has(id)));
    });
  }, [filteredDataRemarks]);

  // ─── Facet counts ────────────────────────────────────────────────────────
  const categoryFacetCount = (cat: CategoryFilter) =>
    dataRemarks.filter(
      r =>
        categoryKey(r.category) === cat &&
        activeStatuses.has(getRemarkStatusKey(r, auth, mode)) &&
        (activeSchedules.size === 0 || getScheduleKeysForRemark(r, subforms).some(k => activeSchedules.has(k)))
    ).length;

  const statusFacetCount = (status: StatusFilter) =>
    dataRemarks.filter(
      r =>
        getRemarkStatusKey(r, auth, mode) === status &&
        activeCategories.has(categoryKey(r.category)) &&
        (activeSchedules.size === 0 || getScheduleKeysForRemark(r, subforms).some(k => activeSchedules.has(k)))
    ).length;

  const scheduleFacetCount = (key: string) =>
    dataRemarks.filter(
      r =>
        getScheduleKeysForRemark(r, subforms).includes(key) &&
        activeCategories.has(categoryKey(r.category)) &&
        activeStatuses.has(getRemarkStatusKey(r, auth, mode))
    ).length;

  // ─── Bulk helpers ────────────────────────────────────────────────────────
  const selectedRemarks = useMemo(() => dataRemarks.filter(r => selectedIds.has(r.id!)), [dataRemarks, selectedIds]);

  const bulkResolvable = selectedRemarks.filter(r => !r.isResolved);
  const bulkSkipResolve = selectedRemarks.filter(r => r.isResolved);
  const bulkCommentable = selectedRemarks.filter(r => !r.isResolved);
  const bulkSkipComment = selectedRemarks.filter(r => r.isResolved);

  const openBulkAction = (type: BulkActionType) => {
    setBulkResult(null);
    setBulkResolutionNotes('');
    setBulkCommentText('');
    setBulkAttachmentFile(null);
    setBulkAction(type);
  };

  const closeBulkModal = () => {
    setBulkAction(null);
    setBulkResult(null);
  };

  const executeBulkAction = async () => {
    if (!bulkAction) return;
    setBulkLoading(true);
    setBulkResult(null);
    try {
      if (bulkAction === 'resolve') {
        const ids = bulkResolvable.map(r => r.id!);
        const { resolved, skipped } = await handleBulkResolve(ids, bulkResolutionNotes);
        setBulkResult({ type: 'success', message: t`${resolved} remarks resolved. ${skipped} skipped.` });
        setSelectedIds(new Set());
      } else {
        const ids = bulkCommentable.map(r => r.id!);
        let attachmentId: number | undefined;
        if (bulkAttachmentFile) {
          const uploaded = await FileApi.uploadFile({ file: bulkAttachmentFile, entityId: entity?.id, wfAccessToken });
          attachmentId = uploaded.id;
        }
        const { commented, skipped } = await handleBulkComment(ids, bulkCommentText, attachmentId);
        setBulkResult({ type: 'success', message: t`Comment added to ${commented} remarks. ${skipped} skipped.` });
        setSelectedIds(new Set());
      }
    } catch (err) {
      setBulkResult({ type: 'error', message: toErrorMessage(err) || t`An error occurred.` });
    } finally {
      setBulkLoading(false);
    }
  };

  if (loading) {
    return (
      <Loader fullscreen>
        <Trans>Loading flags...</Trans>
      </Loader>
    );
  }

  const sharedViewProps: SharedViewProps = {
    dataRemarks: filteredDataRemarks,
    allDataRemarks: dataRemarks,
    selectedRemark,
    setSelectedRemark,
    navigateToField,
    availableFieldsByPath,
    availableFieldsByDataPath,
    canAddRemarks,
    canEditRemarks,
    canResolveRemarks,
    canRespondToRemarks,
    onAddRemark: openCreateModal,
    handleEditRemark,
    handleResolveRemark,
    onAddMessage: handleAddMessageToRemark,
    currentUserId: auth?.user?.user?.id,
    wfAccessToken,
    activeCategories,
    toggleCategory,
    activeStatuses,
    toggleStatus,
    activeSchedules,
    toggleSchedule,
    availableSchedules,
    clearFilters,
    isFiltersActive,
    facetCounts: { category: categoryFacetCount, status: statusFacetCount, schedule: scheduleFacetCount },
    panelMode,
    selectedIds,
    setSelectedIds,
    onBulkResolve: () => openBulkAction('resolve'),
    onBulkComment: () => openBulkAction('comment'),
    getFieldValueForPath,
    mode,
    categoryFilters: CATEGORY_FILTERS,
  };

  return (
    <>
      <UWEEntityDataRemarksSidePanelComponent {...sharedViewProps} />

      {/* ─── Single-remark modals ─────────────────────────────────────── */}
      <DataRemarkModal
        show={showCreateModal}
        onClose={handleModalClose}
        newRemark={newRemark}
        setNewRemark={setNewRemark}
        handleCreateRemark={editingRemark ? handleUpdateRemark : handleCreateRemark}
        isEditing={!!editingRemark}
        editingRemark={editingRemark}
        availableFieldsByPath={availableFieldsByPath}
        availableFieldsByDataPath={availableFieldsByDataPath}
        getFieldValueForPath={getFieldValueForPath}
        onAddMessage={
          editingRemark
            ? async p => {
                await handleAddMessageToEditingRemark(p);
              }
            : undefined
        }
        onResolve={
          canResolveRemarks && editingRemark
            ? () => {
                const id = editingRemark.id!;
                handleModalClose();
                handleResolveRemark(id);
              }
            : undefined
        }
        fieldsDisabled={mode.includes('response') || editingRemark?.isResolved}
        currentUserId={auth?.user?.user?.id}
        wfAccessToken={wfAccessToken}
        modalLoading={modalLoading}
        error={toErrorMessage(errorCreatingRemark)}
        mode={mode}
      />

      <ResolveDataRemarkModal
        show={showResolveModal}
        onClose={() => {
          setShowResolveModal(false);
          setRemarkToResolve(null);
        }}
        onSubmit={handleSubmitResolution}
        remark={remarkToResolve}
        modalLoading={modalLoading}
        error={toErrorMessage(errorResolvingRemark)}
      />

      {/* ─── Bulk action modal ────────────────────────────────────────── */}
      <Modal isOpen={bulkAction !== null} toggle={bulkLoading ? undefined : closeBulkModal} size="md">
        <ModalHeader toggle={bulkLoading ? undefined : closeBulkModal}>
          {bulkAction === 'resolve' && <Trans>Bulk Mark Resolved</Trans>}
          {bulkAction === 'comment' && <Trans>Bulk Add Comment</Trans>}
        </ModalHeader>
        <ModalBody>
          {bulkResult ? (
            <Alert color={bulkResult.type === 'success' ? 'success' : 'danger'} className="mb-3">
              {bulkResult.message}
            </Alert>
          ) : null}

          {bulkAction === 'resolve' && (
            <div>
              <p className="small text-muted mb-2">
                <Trans>{bulkResolvable.length} flags will be marked resolved.</Trans>
                {bulkSkipResolve.length > 0 && (
                  <span className="ml-1 text-warning">
                    <Trans>{bulkSkipResolve.length} already resolved and will be skipped.</Trans>
                  </span>
                )}
              </p>
              <label className="small-label">
                <Trans>Resolution Notes (optional)</Trans>
              </label>
              <Input
                type="textarea"
                rows={3}
                value={bulkResolutionNotes}
                onChange={e => setBulkResolutionNotes(e.target.value)}
                placeholder={t`Optional notes about the resolution`}
                disabled={bulkLoading}
                {...getTestId('data-remarks', '.', 'bulk-resolution-notes')}
              />
            </div>
          )}

          {bulkAction === 'comment' && (
            <div>
              <p className="small text-muted mb-2">
                <Trans>This comment will be added to {bulkCommentable.length} flags.</Trans>
                {bulkSkipComment.length > 0 && (
                  <span className="ml-1 text-warning">
                    <Trans>{bulkSkipComment.length} resolved and will be skipped.</Trans>
                  </span>
                )}
              </p>
              <label className="small-label">
                <Trans>Comment</Trans> <span className="text-danger">*</span>
              </label>
              <div {...getTestId('data-remarks', '.', 'bulk-comment-text')}>
                <InlineCompose
                  value={bulkCommentText}
                  onChange={e => setBulkCommentText(e.target.value)}
                  placeholder={t`Enter your comment`}
                  rows={3}
                  file={bulkAttachmentFile}
                  onFileSelected={setBulkAttachmentFile}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  disabled={bulkLoading}
                  maxLength={MAX_DATA_REMARK_MESSAGE_LENGTH}
                />
              </div>
              <p className="small text-muted mt-1 mb-0">
                <Trans>The attachment (if any) will be added to every comment above.</Trans>
              </p>
            </div>
          )}

          <div className="flex gap-2 justify-end mt-3">
            {bulkResult?.type === 'success' ? (
              <Button color="secondary" onClick={closeBulkModal} {...getTestId('data-remarks', '.', 'bulk-modal-close')}>
                <Trans>Close</Trans>
              </Button>
            ) : (
              <>
                <Button color="secondary" onClick={closeBulkModal} disabled={bulkLoading} {...getTestId('data-remarks', '.', 'bulk-modal-cancel')}>
                  <Trans>Cancel</Trans>
                </Button>
                <Button
                  color="primary"
                  onClick={executeBulkAction}
                  {...getTestId('data-remarks', '.', 'bulk-modal-confirm')}
                  disabled={
                    bulkLoading ||
                    (bulkAction === 'comment' && !bulkCommentText.trim()) ||
                    (bulkAction === 'comment' && bulkCommentText.length > MAX_DATA_REMARK_MESSAGE_LENGTH) ||
                    (bulkAction === 'resolve' && bulkResolvable.length === 0) ||
                    (bulkAction === 'comment' && bulkCommentable.length === 0)
                  }
                >
                  {bulkLoading ? (
                    <span className="flex items-center gap-1">
                      <Spinner size="sm" />
                      <Trans>Processing...</Trans>
                    </span>
                  ) : bulkAction === 'resolve' ? (
                    <Trans>Mark Resolved</Trans>
                  ) : (
                    <Trans>Add Comment</Trans>
                  )}
                </Button>
              </>
            )}
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}

// ─── Shared prop types ────────────────────────────────────────────────────────

type SharedViewProps = {
  dataRemarks: DataRemark[];
  allDataRemarks: DataRemark[];
  selectedRemark: DataRemark | null;
  setSelectedRemark: (r: DataRemark | null) => void;
  navigateToField: (fieldId: string, color?: string) => void;
  availableFieldsByPath: Record<string, FormField>;
  availableFieldsByDataPath: Record<string, FormField>;
  canAddRemarks: boolean;
  canEditRemarks: boolean;
  canResolveRemarks: boolean;
  canRespondToRemarks: boolean;
  onAddRemark: () => void;
  handleEditRemark: (id: number) => void;
  handleResolveRemark: (id: number) => void;
  onAddMessage: (remarkId: number, payload: { message: string; file?: File | null }) => Promise<any>;
  currentUserId?: number;
  wfAccessToken: WFAccessToken;
  // filters
  activeCategories: Set<CategoryFilter>;
  toggleCategory: (cat: CategoryFilter) => void;
  activeStatuses: Set<StatusFilter>;
  toggleStatus: (s: StatusFilter) => void;
  activeSchedules: Set<string>;
  toggleSchedule: (key: string) => void;
  availableSchedules: ScheduleOption[];
  clearFilters: () => void;
  isFiltersActive: boolean;
  facetCounts: {
    category: (cat: CategoryFilter) => number;
    status: (status: StatusFilter) => number;
    schedule: (key: string) => number;
  };
  // panel layout mode (from SidebarComponents)
  panelMode: PanelMode;
  // bulk
  selectedIds: Set<number>;
  setSelectedIds: Dispatch<SetStateAction<Set<number>>>;
  onBulkResolve: () => void;
  onBulkComment: () => void;
  getFieldValueForPath: (path: string) => any;
  mode: string;
  categoryFilters: { id: CategoryFilter; label: string }[];
};

// ─── NavigateToFieldButton ────────────────────────────────────────────────────

type NavigateToFieldButtonProps = {
  remark: DataRemark;
  availableFieldsByPath: Record<string, FormField>;
  availableFieldsByDataPath: Record<string, FormField>;
  navigateToField: (fieldId: string, color?: string) => void;
  stopPropagation?: boolean;
};

function NavigateToFieldButton({
  remark,
  availableFieldsByPath,
  availableFieldsByDataPath,
  navigateToField,
  stopPropagation = false,
}: NavigateToFieldButtonProps) {
  const theme = (useTheme() as unknown) as UWETheme;
  const { isDarkMode } = useDarkMode();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const buttonColor = isDarkMode ? '#ffffff' : theme.primaryMain;

  if (isGeneralRemark(remark)) return null;

  const paths = Array.isArray(remark.fieldPath) ? remark.fieldPath : [remark.fieldPath];
  const names = Array.isArray(remark.fieldName) ? remark.fieldName : [remark.fieldName];

  const fieldEntries = paths.map((path, i) => {
    const field = availableFieldsByPath?.[path] ?? availableFieldsByDataPath?.[path];
    return {
      path,
      name: field?.name ?? names[i] ?? path,
      field,
    };
  });

  if (paths.length <= 1) {
    const handleClick: MouseEventHandler = e => {
      if (stopPropagation) e.stopPropagation();
      const entry = fieldEntries[0];
      if (entry?.field) navigateToField(entry.field.id, 'danger');
    };
    return (
      <HoverLabelButton
        $color={buttonColor}
        type="button"
        onClick={handleClick}
        title={t`Navigate to field`}
        {...getTestId('data-remarks', '.', 'navigate-to-field')}
      >
        <Icon icon="Crosshair" style={{ fontSize: '0.75rem' }} />
        <span className="hover-label">
          <Trans>Go to field</Trans>
        </span>
      </HoverLabelButton>
    );
  }

  const navigableEntries = fieldEntries.filter(e => e.field != null);
  if (navigableEntries.length === 0) return null;

  return (
    <Dropdown
      isOpen={dropdownOpen}
      toggle={(e?: React.MouseEvent) => {
        if (stopPropagation && e) e.stopPropagation();
        setDropdownOpen(v => !v);
      }}
      size="sm"
    >
      <DropdownToggle
        className="simple-dropdown-toggle border-transparent"
        tag={HoverLabelButton as any}
        color={buttonColor}
        type="button"
        title={t`Navigate to field`}
        {...getTestId('data-remarks', '.', 'navigate-to-field-toggle')}
      >
        <Icon icon="Crosshair" style={{ fontSize: '0.75rem' }} />
        <span className="hover-label">
          <Trans>Go to field</Trans>
        </span>
      </DropdownToggle>
      <DropdownMenu>
        {navigableEntries.map(({ path, name, field }, idx) => (
          <DropdownItem
            key={path}
            className="flex items-center gap-1"
            onClick={(e: React.MouseEvent) => {
              if (stopPropagation) e.stopPropagation();
              navigateToField(field!.id, 'danger');
            }}
            {...getTestId('data-remarks', '.', 'navigate-to-field', '-', idx + 1)}
          >
            <Icon icon="Crosshair" className="mr-1" style={{ fontSize: '0.75rem' }} />
            {name}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
}

// ─── Select-all checkbox + bulk row ──────────────────────────────────────────

type SelectAllBulkRowProps = {
  allVisibleSelected: boolean;
  someSelected: boolean;
  onToggleSelectAll: () => void;
  selectedCount: number;
  onBulkResolve: () => void;
  onBulkComment: () => void;
  onClearSelection: () => void;
  canRespond: boolean;
  canResolve: boolean;
};

function SelectAllBulkRow({
  allVisibleSelected,
  someSelected,
  onToggleSelectAll,
  selectedCount,
  onBulkResolve,
  onBulkComment,
  onClearSelection,
  canRespond,
  canResolve,
}: SelectAllBulkRowProps) {
  const theme = (useTheme() as unknown) as UWETheme;
  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = someSelected && !allVisibleSelected;
    }
  }, [someSelected, allVisibleSelected]);

  return (
    <div className="flex items-center mb-1 mt-1" style={{ minHeight: 32 }}>
      <div className="flex items-center" style={{ cursor: 'pointer' }} onClick={onToggleSelectAll}>
        <input
          ref={checkboxRef}
          type="checkbox"
          checked={allVisibleSelected}
          onChange={() => {}}
          style={{ accentColor: theme.primaryMain, width: 15, height: 15, cursor: 'pointer' }}
        />
        <span className="small text-muted">
          <Trans>Select all</Trans>
        </span>
      </div>
      <div className="ml-auto">
        <BulkToolbar
          selectedCount={selectedCount}
          onMarkResolved={onBulkResolve}
          onComment={onBulkComment}
          onClearSelection={onClearSelection}
          canRespond={canRespond}
          canResolve={canResolve}
        />
      </div>
    </div>
  );
}

// ─── Filter bar ───────────────────────────────────────────────────────────────

type FilterBarProps = Pick<
  SharedViewProps,
  | 'activeCategories'
  | 'toggleCategory'
  | 'activeStatuses'
  | 'toggleStatus'
  | 'activeSchedules'
  | 'toggleSchedule'
  | 'availableSchedules'
  | 'clearFilters'
  | 'isFiltersActive'
  | 'facetCounts'
  | 'panelMode'
> & {
  categoryFilters: { id: CategoryFilter; label: string }[];
};

function RemarkFilterBar({
  activeCategories,
  toggleCategory,
  activeStatuses,
  toggleStatus,
  activeSchedules,
  toggleSchedule,
  availableSchedules,
  clearFilters,
  isFiltersActive,
  facetCounts,
  panelMode,
  categoryFilters,
}: FilterBarProps) {
  const { isDarkMode } = useDarkMode();
  const [menuPortalTarget, setMenuPortalTarget] = useState<any>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const statusFilters = getStatusFilters();

  const labelStyle = { ...FILTER_GROUP_LABEL, color: isDarkMode ? '#878b9a' : '#6c757d' };

  const scheduleSelectOptions = availableSchedules.map(s => ({
    value: s.value,
    label: `${s.label} (${facetCounts.schedule(s.value)})`,
  }));

  const scheduleSelectValue = scheduleSelectOptions.filter(o => activeSchedules.has(o.value));

  const scheduleGroup = availableSchedules.length > 0 && (
    <>
      <div className="flex gap-2 items-center" style={{ flexGrow: panelMode === 'bottom' ? 0 : 1 }}>
        <div style={labelStyle}>
          <Trans>Schedule</Trans>
        </div>
        <div style={{ minWidth: 180, flexGrow: 1 }}>
          <Select
            isMulti
            options={scheduleSelectOptions}
            value={scheduleSelectValue}
            onChange={selected => {
              const nextKeys = new Set(((selected as unknown) as { value: string }[]).map(o => o.value));
              const toAdd = [...nextKeys].filter(k => !activeSchedules.has(k));
              const toRemove = [...activeSchedules].filter(k => !nextKeys.has(k));
              toAdd.forEach(toggleSchedule);
              toRemove.forEach(toggleSchedule);
            }}
            placeholder={t`All schedules`}
            classNamePrefix="react-select"
            className="react-select-container w-full"
            isSearchable
            menuPortalTarget={(menuPortalTarget ?? document).body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <Button
          color="secondary"
          size="sm"
          outline
          disabled={!isFiltersActive}
          onClick={clearFilters}
          className="flex items-center gap-1"
          {...getTestId('data-remarks', '.', 'clear-filters')}
        >
          <Icon icon="X" weight="regular" />
          <Trans>Clear filters</Trans>
        </Button>
      </div>
    </>
  );

  if (panelMode === 'bottom') {
    return (
      <div
        ref={containerRef}
        style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', justifyContent: 'space-between' }}
      >
        <div className="flex gap-2 items-center flex-wrap">
          <div style={labelStyle}>
            <Trans>Category</Trans>
          </div>
          <div className="remarks-filter-row">
            {categoryFilters.map(({ id: cat, label }) => (
              <FilterChip
                key={cat}
                color={CATEGORY_COLORS[cat]}
                active={activeCategories.has(cat)}
                onClick={() => toggleCategory(cat)}
                {...getTestId('data-remarks', '.', 'filter-category', '-', cat.toLowerCase().replace(/\s+/g, '-'))}
              >
                <span>{facetCounts.category(cat)}</span>
                {label}
              </FilterChip>
            ))}
          </div>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div style={labelStyle}>
            <Trans>Status</Trans>
          </div>
          <div className="remarks-filter-row">
            {statusFilters.map(({ id: status, label }) => (
              <FilterChip
                key={status}
                color={STATUS_COLORS[status]}
                active={activeStatuses.has(status)}
                onClick={() => toggleStatus(status)}
                {...getTestId('data-remarks', '.', 'filter-status', '-', status.toLowerCase())}
              >
                <span>{facetCounts.status(status)}</span>
                {label}
              </FilterChip>
            ))}
          </div>
        </div>
        {scheduleGroup}
      </div>
    );
  }

  return (
    <div
      ref={el => {
        containerRef.current = el;
        setMenuPortalTarget(el?.ownerDocument);
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}
    >
      <div className="flex gap-2 items-center flex-wrap">
        <div style={labelStyle}>
          <Trans>Category</Trans>
        </div>
        <div className="remarks-filter-row">
          {categoryFilters.map(({ id: cat, label }) => (
            <FilterChip
              key={cat}
              color={CATEGORY_COLORS[cat]}
              active={activeCategories.has(cat)}
              onClick={() => toggleCategory(cat)}
              {...getTestId('data-remarks', '.', 'filter-category', '-', cat.toLowerCase().replace(/\s+/g, '-'))}
            >
              <span>{facetCounts.category(cat)}</span>
              {label}
            </FilterChip>
          ))}
        </div>
      </div>
      <div className="flex gap-2 items-center flex-wrap">
        <div style={labelStyle}>
          <Trans>Status</Trans>
        </div>
        <div className="remarks-filter-row">
          {statusFilters.map(({ id: status, label }) => (
            <FilterChip
              key={status}
              color={STATUS_COLORS[status]}
              active={activeStatuses.has(status)}
              onClick={() => toggleStatus(status)}
              pill
              {...getTestId('data-remarks', '.', 'filter-status', '-', status.toLowerCase())}
            >
              <span>{facetCounts.status(status)}</span>
              {label}
            </FilterChip>
          ))}
        </div>
      </div>
      {(availableSchedules.length > 0 || isFiltersActive) && <div className="flex gap-2 items-center flex-wrap">{scheduleGroup}</div>}
    </div>
  );
}

// ─── Bulk toolbar ─────────────────────────────────────────────────────────────

type BulkToolbarProps = {
  selectedCount: number;
  onMarkResolved: () => void;
  onComment: () => void;
  onClearSelection: () => void;
  canRespond: boolean;
  canResolve: boolean;
};

function BulkToolbar({ selectedCount, onMarkResolved, onComment, onClearSelection, canRespond, canResolve }: BulkToolbarProps) {
  const theme = (useTheme() as unknown) as UWETheme;
  if (selectedCount === 0) return null;
  return (
    <div className="flex items-center gap-1">
      <span className="small text-muted mr-1">
        {selectedCount} <Trans>selected</Trans>
      </span>
      <HoverLabelButton
        $color={theme.secondaryMain ?? '#6c757d'}
        onClick={onClearSelection}
        type="button"
        title={t`Clear selection`}
        {...getTestId('data-remarks', '.', 'bulk-clear-selection')}
      >
        <Icon icon="X" weight="regular" />
        <span className="hover-label">
          <Trans>Clear</Trans>
        </span>
      </HoverLabelButton>
      {canResolve && (
        <HoverLabelButton
          $color={theme.successMain}
          onClick={onMarkResolved}
          type="button"
          title={t`Mark Resolved`}
          {...getTestId('data-remarks', '.', 'bulk-mark-resolved')}
        >
          <Icon icon="Check" />
          <span className="hover-label">
            <Trans>Mark Resolved</Trans>
          </span>
        </HoverLabelButton>
      )}
      {canRespond && (
        <HoverLabelButton
          $color={theme.infoMain}
          onClick={onComment}
          type="button"
          title={t`Comment`}
          {...getTestId('data-remarks', '.', 'bulk-comment')}
        >
          <Icon icon="ChatCentered" />
          <span className="hover-label">
            <Trans id="comment.action">Comment</Trans>
          </span>
        </HoverLabelButton>
      )}
    </div>
  );
}

// ─── Remark accordion item ────────────────────────────────────────────────────

type RemarkAccordionItemProps = {
  remark: DataRemark;
  isOpen: boolean;
  onToggle: () => void;
  availableFieldsByPath: Record<string, FormField>;
  availableFieldsByDataPath: Record<string, FormField>;
  navigateToField: (fieldId: string, color?: string) => void;
  isSelected: boolean;
  onToggleSelect: (e: React.MouseEvent) => void;
  canEditRemarks: boolean;
  canResolveRemarks: boolean;
  canRespondToRemarks: boolean;
  handleEditRemark: (id: number) => void;
  handleResolveRemark: (id: number) => void;
  onAddMessage: (remarkId: number, payload: { message: string; file?: File | null }) => Promise<any>;
  currentUserId?: number;
  wfAccessToken: WFAccessToken;
  getFieldValueForPath: (path: string) => any;
  mode: string;
};

function RemarkAccordionItem({
  remark,
  isOpen,
  onToggle,
  availableFieldsByPath,
  availableFieldsByDataPath,
  navigateToField,
  isSelected,
  onToggleSelect,
  canEditRemarks,
  canResolveRemarks,
  canRespondToRemarks,
  handleEditRemark,
  handleResolveRemark,
  onAddMessage,
  currentUserId,
  wfAccessToken,
  getFieldValueForPath,
  mode,
}: RemarkAccordionItemProps) {
  const { i18n } = useLingui();
  const auth = useContext(AuthContext);
  const theme = (useTheme() as unknown) as UWETheme;
  const { isDarkMode } = useDarkMode();
  const statusKey = getRemarkStatusKey(remark, auth, mode);
  const leftAccentColor = statusKey === 'Resolved' ? theme.successMain : statusKey === 'Responded' ? theme.warningMain : theme.dangerMain;
  const { color: statusBadgeColor, label: statusLabel } = remarkStatus(remark, auth, mode);

  const contentBg = isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';

  const navigateBtnColor = isDarkMode ? '#ffffff' : theme.primaryMain;

  const prefixEl = (
    <input
      type="checkbox"
      checked={isSelected}
      onChange={() => {}}
      onClick={onToggleSelect}
      style={{ accentColor: theme.primaryMain, width: 15, height: 15, cursor: 'pointer' }}
    />
  );

  const titleFields: Array<{ name: string; path: string; value: any }> = (() => {
    const raw = (remark as any)['_fields'];
    if (!raw) return [];
    return Array.isArray(raw) ? raw : [raw];
  })();

  const categoryLabel = getRemarkCategoryLabel(remark.category);

  const titleEl = (
    <div>
      {remark.message}
      <div className="text-muted mb-2 small">
        {isGeneralRemark(remark) && (
          <p className="small mb-0">
            <Trans>Other</Trans>
          </p>
        )}
        {titleFields.slice(0, 2).map((drf, ix) => {
          const actualField = availableFieldsByDataPath?.[drf.path] ?? availableFieldsByPath?.[drf.path];

          if (actualField) {
            return (
              <React.Fragment key={`${drf.name}-${ix}`}>
                <p className="small mb-0">{actualField.description}</p>
                <em className="small mb-1">{actualField.name}</em>
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={`${drf.name}-${ix}`}>
              {drf.name.split(/ \((?=[^(]*$)/).map((str, splitIx) =>
                splitIx > 0 ? (
                  <em className={`small mb-1`} key={str}>
                    {str.replace(')', '')}
                  </em>
                ) : (
                  <p className={`small mb-0`} key={str}>
                    {str}
                  </p>
                )
              )}
            </React.Fragment>
          );
        })}
        {titleFields.length > 2 && (
          <div className="small text-muted mb-1">
            ...+{titleFields.length - 2} <Trans>more (expand for details)</Trans>
          </div>
        )}
      </div>
      <div className="flex gap-1 items-center flex-wrap">
        <CustomBadge color={CATEGORY_COLORS[categoryKey(remark.category)]} style={{ fontSize: '0.65rem' }}>
          {categoryLabel}
        </CustomBadge>
        <CustomBadge color={STATUS_COLORS[statusKey]} style={{ fontSize: '0.65rem' }} pill>
          {statusLabel}
        </CustomBadge>
        {canEditRemarks && (
          <HoverLabelButton
            $color={theme.primaryMain ?? '#6c757d'}
            onClick={e => {
              e.stopPropagation();
              handleEditRemark(remark.id!);
            }}
            type="button"
            title={t`Edit`}
            {...getTestId('data-remarks', '.', 'remark-edit', '-', remark.id)}
          >
            <Icon icon="NotePencil" />
            <span className="hover-label">
              <Trans>Edit</Trans>
            </span>
          </HoverLabelButton>
        )}
        <NavigateToFieldButton
          remark={remark}
          availableFieldsByPath={availableFieldsByPath}
          availableFieldsByDataPath={availableFieldsByDataPath}
          navigateToField={navigateToField}
          stopPropagation
        />
      </div>
    </div>
  );

  return (
    <AccordionItem
      id={`dataremark-item-card-${remark.id}`}
      itemId={String(remark.id)}
      isOpen={isOpen}
      toggle={() => onToggle()}
      prefixContent={prefixEl}
      title={titleEl}
      leftAccentColor={leftAccentColor}
      selectionBorderColor={isSelected ? theme.primaryMain : undefined}
      className={classnames('mb-2', { active: isSelected })}
    >
      <div className="dataremark-details border-top">
        {/* Content area — tinted background */}
        <div className="px-3 pt-2 pb-3" style={{ background: contentBg }}>
          {/* Row 1: description */}
          {remark.description && (
            <div className="mb-3">
              <strong className="small text-muted">
                <Trans>Description</Trans>
              </strong>
              <p className="mb-0 small">{remark.description}</p>
            </div>
          )}

          {remark.isResolved && (
            <div className="mb-3 p-2 bg-opacity-10 rounded">
              <div className="flex items-center mb-2 gap-1">
                <Icon icon="CheckCircle" className="text-success mr-2" />
                <strong className="small text-muted">
                  <Trans>Resolution Notes</Trans>
                </strong>
              </div>
              <p className="mb-2 small">{remark.resolutionNotes}</p>
              <small className="text-muted">
                {remark.resolvedBy?.name} —{' '}
                {dayjs(remark.resolvedAt)
                  .locale(i18n.locale)
                  .format('lll')}
              </small>
            </div>
          )}

          {/* Row 2: created by + created at */}
          <div className="mb-3 flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <strong className="small text-muted">
                <Trans>Created By:</Trans>
              </strong>
              <span className="ml-1 small">{remark.createdBy ? remark.createdBy.name : <Trans>OCFI</Trans>}</span>
            </div>
            <div className="flex items-center gap-1">
              <strong className="small text-muted">
                <Trans>Created At:</Trans>
              </strong>
              <span className="ml-1 small">
                {dayjs(remark.createdAt)
                  .locale(i18n.locale)
                  .format('lll')}
              </span>
            </div>
          </div>

          {/* Row 3: values table */}
          {renderValuesTable(remark, getFieldValueForPath, availableFieldsByDataPath, availableFieldsByPath, navigateToField, navigateBtnColor)}

          {/* Row 4: messages */}
          {(!remark.isResolved || (remark.messages?.length ?? 0) > 0) && (
            <MessageThread
              messages={remark.messages ?? []}
              currentUserId={currentUserId}
              remarkCreatedById={remark.createdBy?.id}
              responseMode={canRespondToRemarks}
              wfAccessToken={wfAccessToken}
              onSend={onAddMessage ? p => onAddMessage(remark.id!, p) : undefined}
              readonly={remark.isResolved}
            />
          )}
        </div>

        {/* Row 5: footer buttons — transparent background */}
        {!remark.isResolved && (canResolveRemarks || canEditRemarks) && (
          <div className="px-3 pt-2 pb-3 border-top flex gap-2 flex-wrap" style={{ justifyContent: 'flex-end' }}>
            {canEditRemarks && (
              <Button
                size="sm"
                color="primary"
                className="flex items-center gap-1"
                onClick={e => {
                  e.stopPropagation();
                  handleEditRemark(remark.id!);
                }}
                {...getTestId('data-remarks', '.', 'remark-edit', '-', remark.id)}
              >
                <Icon icon="NotePencil" className="mr-1" />
                <Trans>Edit</Trans>
              </Button>
            )}
            {canResolveRemarks && (
              <Button
                size="sm"
                color="success"
                className="flex items-center gap-1"
                onClick={e => {
                  e.stopPropagation();
                  handleResolveRemark(remark.id!);
                }}
                {...getTestId('data-remarks', '.', 'remark-resolve', '-', remark.id)}
              >
                <Icon icon="Check" className="mr-1" />
                <Trans>Mark Resolved</Trans>
              </Button>
            )}
          </div>
        )}
      </div>
    </AccordionItem>
  );
}

// ─── Side-panel view ──────────────────────────────────────────────────────────

function UWEEntityDataRemarksSidePanelComponent(props: SharedViewProps) {
  const {
    dataRemarks,
    allDataRemarks,
    selectedRemark,
    setSelectedRemark,
    navigateToField,
    availableFieldsByPath,
    availableFieldsByDataPath,
    canAddRemarks,
    canEditRemarks,
    canResolveRemarks,
    canRespondToRemarks,
    onAddRemark,
    handleEditRemark,
    handleResolveRemark,
    onAddMessage,
    currentUserId,
    wfAccessToken,
    activeCategories,
    toggleCategory,
    activeStatuses,
    toggleStatus,
    activeSchedules,
    toggleSchedule,
    availableSchedules,
    clearFilters,
    isFiltersActive,
    facetCounts,
    panelMode,
    selectedIds,
    setSelectedIds,
    onBulkResolve,
    onBulkComment,
    getFieldValueForPath,
    mode,
    categoryFilters,
  } = props;

  const auth = useContext(AuthContext);
  const hasNoData = allDataRemarks.length === 0;
  const allVisibleSelected = dataRemarks.length > 0 && dataRemarks.every(r => selectedIds.has(r.id!));
  const someSelected = selectedIds.size > 0;

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        dataRemarks.forEach(r => next.delete(r.id!));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        dataRemarks.forEach(r => next.add(r.id!));
        return next;
      });
    }
  };

  return (
    <div className="uweentity-dataremarks-partial">
      {/* Row 1: title + badge + add button */}
      <div className="flex items-center mb-1 gap-2 flex-wrap">
        <span className="font-bold" style={{ fontSize: '1rem' }}>
          <Trans>Flags</Trans>
        </span>
        <CustomBadge color="primary">{allDataRemarks.length}</CustomBadge>
        {canAddRemarks && (
          <Button
            className="flex gap-1 items-center ml-auto"
            color="primary"
            size="sm"
            onClick={onAddRemark}
            {...getTestId('data-remarks', '.', 'new-finding')}
          >
            <Icon icon="Plus" weight="regular" className="mr-1" />
            <Trans>Add Flag</Trans>
          </Button>
        )}
      </div>

      {/* Row 2: filters */}
      <div className="mt-2 mb-1">
        <RemarkFilterBar
          activeCategories={activeCategories}
          toggleCategory={toggleCategory}
          activeStatuses={activeStatuses}
          toggleStatus={toggleStatus}
          activeSchedules={activeSchedules}
          toggleSchedule={toggleSchedule}
          availableSchedules={availableSchedules}
          clearFilters={clearFilters}
          isFiltersActive={isFiltersActive}
          facetCounts={facetCounts}
          panelMode={panelMode}
          categoryFilters={categoryFilters}
        />
      </div>

      {/* Row 3: select all (left) + bulk toolbar (right) */}
      {!hasNoData && (
        <SelectAllBulkRow
          allVisibleSelected={allVisibleSelected}
          someSelected={someSelected}
          onToggleSelectAll={toggleSelectAll}
          selectedCount={selectedIds.size}
          onBulkResolve={onBulkResolve}
          onBulkComment={onBulkComment}
          onClearSelection={() => setSelectedIds(new Set())}
          canRespond={canRespondToRemarks}
          canResolve={canResolveRemarks}
        />
      )}

      {/* Remark list */}
      {dataRemarks.length === 0 ? (
        <EmptyState
          icon={!hasNoData && isFiltersActive ? 'search' : 'check'}
          message={!hasNoData && isFiltersActive ? t`No flags match your current search.` : t`No flags detected`}
          size={120}
          filtersApplied={!hasNoData && isFiltersActive}
          clearFilters={!hasNoData && isFiltersActive ? clearFilters : null}
        />
      ) : (
        <div className="dataremarks-grid-partial">
          {dataRemarks.map(remark => (
            <RemarkAccordionItem
              key={remark.id}
              remark={remark}
              mode={mode}
              isOpen={selectedRemark?.id === remark.id}
              onToggle={() => setSelectedRemark(selectedRemark?.id === remark.id ? null : remark)}
              availableFieldsByPath={availableFieldsByPath}
              availableFieldsByDataPath={availableFieldsByDataPath}
              navigateToField={navigateToField}
              isSelected={selectedIds.has(remark.id!)}
              onToggleSelect={e => {
                e.stopPropagation();
                setSelectedIds(prev => {
                  const next = new Set(prev);
                  next.has(remark.id!) ? next.delete(remark.id!) : next.add(remark.id!);
                  return next;
                });
              }}
              canEditRemarks={canEditRemarks && canEditRemark(remark, auth)}
              canResolveRemarks={canResolveRemarks}
              canRespondToRemarks={canRespondToRemarks}
              handleEditRemark={handleEditRemark}
              handleResolveRemark={handleResolveRemark}
              onAddMessage={onAddMessage}
              currentUserId={currentUserId}
              wfAccessToken={wfAccessToken}
              getFieldValueForPath={getFieldValueForPath}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default UWEEntityDataRemarks;
